import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  CircularProgress,
  Typography,
  Alert
} from '@mui/material';
import { Vessel } from '../models/types';
import { updateVessel } from '../firebase/services/vesselService';

interface VesselRegistrationDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (vessel: Vessel) => Promise<void>;
  onUpdate: () => Promise<void>;
  vessel?: Vessel;
}

// US State abbreviations
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

interface FormData {
  registrationNumber: string;
  stateRegistrationExpiry: string;
  stateRegistrationState: string;
  vesselName: string;
  propulsionType: 'powered' | 'non-powered';
}

const VesselRegistrationDialog: React.FC<VesselRegistrationDialogProps> = ({
  open,
  onClose,
  onSubmit,
  onUpdate,
  vessel,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    registrationNumber: '',
    stateRegistrationExpiry: '',
    stateRegistrationState: '',
    vesselName: '',
    propulsionType: 'powered',
  });

  useEffect(() => {
    if (vessel) {
      setFormData({
        registrationNumber: vessel.registrationNumber || '',
        stateRegistrationExpiry: vessel.stateRegistrationExpiry || '',
        stateRegistrationState: vessel.stateRegistrationState || '',
        vesselName: vessel.name || '',
        propulsionType: vessel.propulsionType || 'powered',
      });
    }
  }, [vessel]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!vessel) return;
    
    setLoading(true);
    setError('');
    setSuccess(false);
    
    try {
      await updateVessel(vessel.id, {
        registrationNumber: formData.registrationNumber,
        stateRegistrationExpiry: formData.stateRegistrationExpiry,
        stateRegistrationState: formData.stateRegistrationState,
        name: formData.vesselName,
        propulsionType: formData.propulsionType,
      });
      
      setSuccess(true);
      onUpdate();
      
      // Close dialog after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to update registration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {vessel?.propulsionType === 'powered' ? 'Powered Vessel' : 'Vessel'} State Registration
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Registration updated successfully!
          </Alert>
        )}
        
        <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 3 }}>
          Update state registration information for {vessel?.name}
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Vessel Name"
              name="vesselName"
              value={formData.vesselName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Registration Number"
              name="registrationNumber"
              value={formData.registrationNumber}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Propulsion Type</InputLabel>
              <Select
                name="propulsionType"
                value={formData.propulsionType}
                onChange={handleSelectChange}
              >
                <MenuItem value="powered">Powered</MenuItem>
                <MenuItem value="non-powered">Non-Powered</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>State</InputLabel>
              <Select
                name="stateRegistrationState"
                value={formData.stateRegistrationState}
                label="State"
                onChange={handleSelectChange}
                disabled={loading}
              >
                {US_STATES.map(state => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Expiry Date"
              name="stateRegistrationExpiry"
              type="date"
              value={formData.stateRegistrationExpiry}
              onChange={handleChange}
              disabled={loading}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Save Registration'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VesselRegistrationDialog; 